import Q from 'q';

import user from 'js/lib/user';

import type {
  GenerateUploadLocationRequest,
  GenerateUploadLocationResponse,
  PreviewImportRequest,
  ProcessImportAsyncRequest,
  ProcessImportSyncRequest,
} from '@coursera/grpc-types-enterpriseuser/coursera/proto/enterpriseuser/bulkprograminvitations/v1/bulk_program_invitations_api';
import type { MultiSendProgramInvitationsRequest } from '@coursera/grpc-types-enterpriseuser/coursera/proto/enterpriseuser/programinvitations/v1/program_invitations_api';

import S3Upload from 'bundles/enterprise-admin-program-users/utils/s3uploader';
import EnterpriseCustomizedMessages from 'bundles/naptimejs/resources/enterpriseCustomizedMessages.v1';
import ProgramInvitationsV1 from 'bundles/naptimejs/resources/programInvitations.v1';

const BULK_PROGRAM_INVITATIONS_GRPC_ROOT =
  '/api/grpc/enterpriseuser/bulkprograminvitations/v1/BulkProgramInvitationsAPI';

const PROGRAM_INVITATIONS_GRPC_ROOT = '/api/grpc/enterpriseuser/programinvitations/v1/ProgramInvitationsAPI';

/**
 * Enclose all ProgramInvitationApi related logic in this class
 * - Share variables across different api calls, e.g. naptime, program, resourceName
 * - Put apiPromise and apiNotification generation logic together, so it's easy to keep track
 * - Provide a way to consistently deal with apiPromise, both for generic promise and Naptime mutations
 * - Allow any component to share the resource, so long as the apiManager instance is passed down
 */
class ProgramInvitationsApiManager {
  declare _programId?: string;

  declare _naptime: $TSFixMe;

  declare _resourceName: string;

  constructor({ program, naptime }: { program?: { id: string }; naptime: $TSFixMe }) {
    this._programId = program?.id;
    this._naptime = naptime;
    this._resourceName = ProgramInvitationsV1.RESOURCE_NAME;
  }

  get programId() {
    return this._programId;
  }

  getRefreshDataPromise() {
    const refreshPromise = () =>
      this._naptime.refreshData({
        resources: [this._resourceName],
      });

    // When the member list is mutated we do not see it immediately due to Cassandra's eventual consistency.
    // We want to fetch twice because the first delay tends to be less than 1 second, which may happen before Cassandra updates the record.
    return Q()
      .delay(1000)
      .then(() => refreshPromise())
      .delay(1000)
      .then(() => refreshPromise());
  }

  getSendInvitationsPromise({ invitationIds }: { invitationIds: string[] }) {
    const request: MultiSendProgramInvitationsRequest = { programInvitationIds: invitationIds };
    return this._postToProgramInvitations('MultiSendProgramInvitations', request);
  }

  async getUploadCSVLocation(callback: (data: GenerateUploadLocationResponse) => void) {
    const request: GenerateUploadLocationRequest = { programId: this._programId, userId: user.get().id };
    const response = await this._postToBulkProgramInvitations('GenerateUploadLocation', request);
    if (response.ok) {
      const responseData: GenerateUploadLocationResponse = await response.json();
      return callback(responseData);
    }
    throw new Error(`Error generating upload location: ${response.statusText}.`);
  }

  uploadFile(file: File) {
    const uploader = new S3Upload({
      getSignedUrl: this.getUploadCSVLocation.bind(this),
      onFinishS3Put(signResult) {
        return signResult;
      },
    });
    return uploader.handleFileSelect(file);
  }

  getPreviewImportPromise({ s3Key, s3Bucket, rosterId }: { s3Key: string; s3Bucket: string; rosterId?: string }) {
    const request: PreviewImportRequest = {
      s3Bucket,
      s3Key,
      programId: this._programId,
      rosterId: rosterId || undefined,
    };
    return this._postToBulkProgramInvitations('PreviewImport', request);
  }

  getProcessInvitationsSynchronouslyPromise({
    s3Key,
    s3Bucket,
    rosterId,
    sendInvitations,
  }: {
    s3Key: string;
    s3Bucket: string;
    rosterId?: string;
    sendInvitations: boolean;
  }) {
    const request: ProcessImportSyncRequest = {
      s3Bucket,
      s3Key,
      programId: this._programId,
      sendInvitations,
      rosterId: rosterId || undefined,
      userId: user.get().id,
    };
    return this._postToBulkProgramInvitations('ProcessImportSync', request);
  }

  getProcessInvitationsAsynchronouslyPromise({
    s3Key,
    s3Bucket,
    rosterId,
    sendInvitations,
  }: {
    s3Key: string;
    s3Bucket: string;
    rosterId?: string;
    sendInvitations: boolean;
  }) {
    const request: ProcessImportAsyncRequest = {
      s3Bucket,
      s3Key,
      sendInvitations,
      rosterId: rosterId || undefined,
      programId: this._programId,
      userId: user.get().id,
    };
    return this._postToBulkProgramInvitations('ProcessImportAsync', request);
  }

  getGenerateEmailMarkupPromise = ({ body, params }: $TSFixMe) => {
    return this._naptime.executeMutation(EnterpriseCustomizedMessages.action('preview', body, params));
  };

  getUpdateProgramCustomMessagePromise({ id, title, cml }: $TSFixMe) {
    const data = {
      title,
      content: cml,
    };
    return () => this._naptime.executeMutation(EnterpriseCustomizedMessages.update(id, data));
  }

  getDeleteProgramCustomMessagePromise({ id }: { id: string }) {
    return () => this._naptime.executeMutation(EnterpriseCustomizedMessages.delete(id));
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  private _postToProgramInvitations(endPoint: string, body: any) {
    return fetch(`${PROGRAM_INVITATIONS_GRPC_ROOT}/${endPoint}`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json' },
    });
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  private _postToBulkProgramInvitations(endPoint: string, body: any) {
    return fetch(`${BULK_PROGRAM_INVITATIONS_GRPC_ROOT}/${endPoint}`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json' },
    });
  }
}

export default ProgramInvitationsApiManager;
